import Enums from "@/enums";

type RoleHistoryColumn = {
    companyRoleId?: number | null;
    name: string;
    order: number;
    columnTitle?: string;
};

type RecentsRole = {
    id: number;
    permissions?: Array<number | { id?: number; permissionId?: number; permId?: number }>;
};

export function roleHasPermission(role: RecentsRole | null | undefined, permId: number) {
    const rolePermissions = role && Array.isArray(role.permissions) ? role.permissions : [];
    return rolePermissions.some((p) => {
        if (typeof p === "number") return Number(p) === permId;
        return Number(p?.id ?? p?.permissionId ?? p?.permId ?? 0) === permId;
    });
}

export function resolveRecentsColumnView(
    roles: RecentsRole[],
    options: { isAdmin: boolean; simRoleId: string | number | null | undefined; currentRoleId: number }
) {
    const { isAdmin, simRoleId, currentRoleId } = options;
    const allRoles = Array.isArray(roles) ? roles : [];
    const rolesForView = isAdmin && simRoleId
        ? allRoles.filter((r) => Number(r.id) === Number(currentRoleId))
        : allRoles;
    const primaryRole = rolesForView[0]
        ?? allRoles.find((r) => Number(r.id) === Number(currentRoleId));

    const hasRoleSystemAdmin = primaryRole
        ? roleHasPermission(primaryRole, Enums.ROLE_PERMS.SystemAdmin)
        : isAdmin && Number(currentRoleId) === Enums.ROLE_PERMS.SystemAdmin;
    const hasRoleReportView = rolesForView.some((role) =>
        roleHasPermission(role, Enums.ROLE_PERMS.ReportView));
    const hasRoleMatchView = rolesForView.some((role) =>
        roleHasPermission(role, Enums.ROLE_PERMS.MatchView));
    const hasRoleTestEdit = rolesForView.some((role) =>
        roleHasPermission(role, Enums.ROLE_PERMS.TestEdit));

    let activeRoleIds: Set<number> | null = null;
    if (!hasRoleSystemAdmin) {
        const roleIds = (isAdmin && simRoleId ? rolesForView : allRoles)
            .map((r) => Number(r.id))
            .filter((id) => !Number.isNaN(id));
        activeRoleIds = new Set(roleIds);
    }

    return {
        rolesForView,
        hasRoleSystemAdmin,
        hasRoleReportView,
        hasRoleMatchView,
        hasRoleTestEdit,
        activeRoleIds,
    };
}

function dedupeColumnsByName(columns: RoleHistoryColumn[]) {
    const byName = new Map<string, RoleHistoryColumn>();
    columns.forEach((col) => {
        const existing = byName.get(col.name);
        if (!existing || col.order < existing.order) {
            byName.set(col.name, col);
        }
    });
    return Array.from(byName.values());
}

function applyStandardColumnFilters(columns: RoleHistoryColumn[], canShowReports: boolean) {
    return columns
        .filter((rhc) => rhc.name !== "TestDelete")
        .filter((rhc) => rhc.name !== "Reports" || canShowReports);
}

/**
 * System Admin view: use only the synthesized null-role template
 * (global "System Admin" HistoryColumnSet or a company SystemAdmin role layout).
 * If that template is empty, fall back to a deduped union of role-scoped columns
 * so the table is not blank.
 */
function buildSysAdminVisibleColumns(
    roleHistoryColumns: RoleHistoryColumn[],
    canShowReports: boolean
) {
    const nullRoleColumns = applyStandardColumnFilters(
        roleHistoryColumns.filter((rhc) => (rhc.companyRoleId ?? null) === null),
        canShowReports
    );

    if (nullRoleColumns.length > 0) {
        return nullRoleColumns.sort((a, b) => a.order - b.order);
    }

    return dedupeColumnsByName(
        applyStandardColumnFilters(
            roleHistoryColumns.filter((rhc) => (rhc.companyRoleId ?? null) !== null),
            canShowReports
        )
    ).sort((a, b) => a.order - b.order);
}

export function buildVisibleRecentsColumns(
    roleHistoryColumns: RoleHistoryColumn[],
    activeRoleIds: Set<number> | null,
    canShowReports: boolean
) {
    if (activeRoleIds === null) {
        return buildSysAdminVisibleColumns(roleHistoryColumns, canShowReports);
    }

    let visible = applyStandardColumnFilters(
        roleHistoryColumns.filter((rhc) => {
            const roleId = rhc.companyRoleId ?? null;
            return roleId !== null && activeRoleIds.has(roleId);
        }),
        canShowReports
    );

    if (activeRoleIds.size > 1) {
        visible = dedupeColumnsByName(visible);
    }

    return visible.sort((a, b) => a.order - b.order);
}
